import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FormlyValueChangeEvent } from '@ngx-formly/core/lib/models';
import { AnalyticsAwareFieldType } from '../analytics-aware-field-type';
import { Subscription, distinctUntilChanged, filter } from 'rxjs';
import { IEnvironment } from '../../../models/environment.model';
import {
  IIntegrationFetchSyncRequest,
  IIntegrationFetchSyncRequestResponse,
} from '../../../models/integration-fetch-sync.model';
import { alphaNanoIdUtil } from '../../../../utils/utils/alpha-nano-id.util';
import { TranslateService } from '@ngx-translate/core';
import { FormStateService } from '../../../services/formly/form-state.service';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
@Component({
  selector: 'irembogov-custom-drop-down',
  templateUrl: './custom-drop-down.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDropDownComponent
  extends AnalyticsAwareFieldType<FieldTypeConfig>
  implements OnInit, OnDestroy, AfterViewInit
{
  items: Record<string, unknown>[] = [];
  private subscriptions = new Subscription();
  private environment: IEnvironment;
  loading = false;
  dataFetchFields: string[] = ['CUSTOM_DROPDOWN_DATAFETCH'];
  totalPages = 0;
  currentPage = 0;

  constructor(
    private readonly http: HttpClient,
    private readonly cd: ChangeDetectorRef,
    private readonly formStateService: FormStateService,
    private readonly translateService: TranslateService,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
  }

  ngOnInit(): void {
    this.getDataSet();
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.field.formControl.valueChanges
        .pipe(
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe({
          next: (value: unknown) => {
            if (value && typeof value !== 'object') {
              if (!this.field.props['bindValue'] && Array.isArray(this.items)) {
                const objValue = this.findBindValue(this.items, value);
                this.field.formControl.setValue(objValue);
              }
            }
          },
        })
    );

    this.subscriptions.add(
      this.field.options?.fieldChanges
        ?.pipe(
          filter((e: FormlyValueChangeEvent) => {
            return (
              e.field.key === this.field.key &&
              e.type === 'expressionChanges' &&
              e['property'] === 'props.options' &&
              e.value
            );
          })
        )
        .subscribe((e: FormlyValueChangeEvent) => {
          this.field.props.options = e.field.props?.options ?? [];
          this.items = <Record<string, unknown>[]>(
            (e.field.props?.options ?? [])
          );
        })
    );

    // Add subscription to watch changes in the datasets
    this.subscriptions.add(
      this.field.options?.fieldChanges
        ?.pipe(
          filter((e: FormlyValueChangeEvent) => {
            return (
              e.field.key === this.field.key &&
              e.type === 'expressionChanges' &&
              (e['property'].startsWith('props.dataset.params') ||
                e['property'] === 'props.options')
            );
          })
        )
        .subscribe((e: FormlyValueChangeEvent) => {
          if (e['property'] === 'props.options') {
            this.field.props.options = e.field.props?.options ?? [];
            this.items = <Record<string, unknown>[]>(
              (e.field.props?.options ?? [])
            );
          } else {
            // Refresh dataset when params change
            this.getDataSet();
          }
        })
    );
  }

  findBindValue(options: Record<string, unknown>[], value: unknown) {
    const lowercaseValue =
      typeof value === 'string' ? value.toLowerCase() : value;
    const result = options.find(obj =>
      Object.values(obj).some(val => {
        if (typeof val === 'string') {
          return val.toLowerCase() === lowercaseValue;
        }
        return val === lowercaseValue;
      })
    );
    return result ?? null;
  }

  updateUrlWithApiGatewayBaseUrl(url: string) {
    const portalApiGatewayBaseUrl: string | undefined =
      this.environment.apiGatewayBaseUrl;
    if (!portalApiGatewayBaseUrl) {
      throw new Error(
        'useBaseUrl: portal environment ApiGatewayBaseUrl property is required'
      );
    } else {
      url = `${portalApiGatewayBaseUrl}${url}`;
    }

    return url;
  }

  // One analytics outcome per logical options load; reset only in getDataSet.
  private hasEmittedOptionsOutcome = false;

  protected emitOptionsFetchOutcome(
    outcome: 'success' | 'failure',
    error?: unknown
  ): void {
    if (this.hasEmittedOptionsOutcome) {
      return;
    }
    this.hasEmittedOptionsOutcome = true;
    this.emitFetchOutcome(outcome, error);
  }

  getDataSet() {
    // Add check for existing request
    if (this.loading) {
      return;
    }

    this.hasEmittedOptionsOutcome = false;

    if (this.field?.props['options']) {
      this.items = this.field.props['options'] as Record<string, unknown>[];
    }

    const { dataset } = this.field?.props || {};
    if (dataset) {
      if (!dataset['url']) {
        throw new Error('Invalid dataset property');
      }

      const httpMethod = dataset?.['httpMethod'];
      let url = dataset['url'];
      const params = { ...dataset['params'] }; // Create a copy of params
      const headers = dataset['headers'];
      const dataField = dataset['dataField'];

      if (this.field.props['useBaseUrl']) {
        url = this.updateUrlWithApiGatewayBaseUrl(url);
      }

      if (httpMethod === 'POST') {
        this.getDataByPostMethod(url, dataField, params, headers);
      } else {
        this.getDataByGetMethod(url, dataField, params, headers);
      }
    }
  }

  getDataByPostMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    totalPagesKey = 'totalPages',
    currentPageKey = 'number'
  ) {
    this.loading = true;
    const fieldSubType = this.field?.props['subType'];
    if (fieldSubType && this.dataFetchFields.includes(fieldSubType)) {
      const endpointCode = this.field?.props['code'];
      this.getDataByIntegrationFetchSync(endpointCode, url, dataField);
    } else {
      this.subscriptions.add(
        this.http
          .post<Record<string, unknown> | []>(url, params, { headers })
          .subscribe({
            next: res => {
              this.processResponse({
                res,
                dataField,
                totalPagesKey,
                currentPageKey,
              });
              this.cd.detectChanges();
              this.emitOptionsFetchOutcome('success');
            },
            error: (error: unknown) => {
              this.loading = false;
              this.deleteFetchedOptionsDataKeyInFormState();
              this.cd.detectChanges();
              this.emitOptionsFetchOutcome('failure', error);
            },
          })
      );
    }
  }

  getDataByGetMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    totalPagesKey = 'totalPages',
    currentPageKey = 'number'
  ) {
    const queryParams = Object.keys(params)
      .map(key => `${key}=${encodeURIComponent(params[key] as string)}`)
      .join('&');

    this.loading = true;
    this.subscriptions.add(
      this.http
        .get<Record<string, unknown> | []>(`${url}?${queryParams}`, { headers })
        .subscribe({
          next: res => {
            this.processResponse({
              res,
              dataField,
              totalPagesKey,
              currentPageKey,
            });
            this.cd.detectChanges();
            this.emitOptionsFetchOutcome('success');
          },
          error: (error: unknown) => {
            this.loading = false;
            this.deleteFetchedOptionsDataKeyInFormState();
            this.cd.detectChanges();
            this.emitOptionsFetchOutcome('failure', error);
          },
        })
    );
  }

  translateDatasetLabels(
    res: Record<string, unknown> | [],
    dataField: string
  ): Record<string, unknown>[] {
    const dasetItems: Record<string, unknown>[] = Array.isArray(res)
      ? res
      : this.getValueFromNestedObject(res, dataField);

    const bindLabel: string = this.field.props['bindLabel'] ?? 'label';
    return dasetItems.map((dataItem: Record<string, unknown>) => {
      if (dataItem[bindLabel]) {
        dataItem[bindLabel] = this.translateService.instant(
          <string>dataItem[bindLabel]
        );
      }

      return dataItem;
    });
  }

  getDataByIntegrationFetchSync(
    endpointCode: string,
    url: string,
    dataField: string
  ): void {
    this.loading = true;
    const req: IIntegrationFetchSyncRequest = {
      callerId: alphaNanoIdUtil(50),
      endpointCode: endpointCode,
      requester: alphaNanoIdUtil(50),
      payload: {
        ServiceName: endpointCode,
      },
    };

    this.subscriptions.add(
      this.http
        .post<IIntegrationFetchSyncRequestResponse>(`${url}`, req)
        .subscribe({
          next: res => {
            this.saveFetchedOptionsDataKeyInFormState(res.data);
            if (res.status) {
              let responseObject = undefined;
              if (typeof res.data.response === 'string') {
                responseObject = JSON.parse(res.data.response);
              } else {
                responseObject = res.data.response;
              }
              if (dataField.length === 0) {
                this.items = responseObject;
              } else {
                this.items = this.getValueFromNestedObject(
                  responseObject,
                  dataField
                );
              }
            }
            this.loading = false;
            this.cd.detectChanges();
            this.emitOptionsFetchOutcome('success');
          },
          error: (error: unknown) => {
            this.loading = false;
            this.deleteFetchedOptionsDataKeyInFormState();
            this.cd.detectChanges();
            this.emitOptionsFetchOutcome('failure', error);
          },
        })
    );
  }

  saveFetchedOptionsDataKeyInFormState(data: any): void {
    this.formStateService.saveFetchedDataKeyInFormState(
      this.field,
      data,
      EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA,
      false
    );
  }

  deleteFetchedOptionsDataKeyInFormState(): void {
    this.formStateService.deleteFetchedDataKeyInFormState(
      this.field,
      EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA
    );
  }

  private getValueFromNestedObject(
    obj: Record<string, unknown>,
    keyString: string
  ) {
    const keys = keyString.split('.');
    let value: any = obj;
    for (const key of keys) {
      let valueForKey = value[key] as Record<string, unknown>[];
      if (valueForKey !== undefined) {
        value = valueForKey;
        continue;
      }

      if (typeof value !== 'string') {
        throw new Error(`Invalid key string: ${keyString}`);
      }
      valueForKey = JSON.parse(value)[key] as Record<string, unknown>[];

      if (valueForKey === undefined) {
        throw new Error(`Invalid key string: ${keyString}`);
      }
      value = valueForKey;
    }

    return Array.isArray(value)
      ? (value as unknown as Record<string, unknown>[])
      : [];
  }

  private processResponse(params: {
    res: Record<string, unknown> | [];
    dataField: string;
    totalPagesKey: string;
    currentPageKey: string;
  }): void {
    this.saveFetchedOptionsDataKeyInFormState(params.res);
    this.items = [
      ...this.items,
      ...this.translateDatasetLabels(params.res, params.dataField),
    ];

    this.loading = false;

    if (!Array.isArray(params.res)) {
      const data = params.res['data'] as Record<string, unknown>;
      this.totalPages = (data[params.totalPagesKey] as number) ?? 0;
      this.currentPage = (data[params.currentPageKey] as number) ?? 0;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
